<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import type { MemberState } from '../../api/routing/types'
import { shortNames } from '../../lib/routing'
import { useInbox } from '../../stores/inbox'
import { useRouting } from '../../stores/routing'
import Icon from '../ui/Icon.vue'

// Footer of the chat list: how chats are handed out, the central queue, and each person's load (live, from
// GET /api/v1/team/load — stores/routing.ts). Until the first answer arrives, the bootstrap team is shown.
const store = useInbox()
const routing = useRouting()
onMounted(() => routing.start())
onUnmounted(() => routing.stop())

const MODE_LABEL = { least_busy: 'ยุ่งน้อยสุด', round_robin: 'วนตามลำดับ', manual: 'ไม่แจกอัตโนมัติ' } as const
const method = computed(() => routing.load?.method ?? store.settings.assign_mode)
const modeLabel = computed(() => routing.load?.method_label ?? MODE_LABEL[method.value] ?? 'ยุ่งน้อยสุด')
const queueCount = computed(() => routing.load?.queue.waiting ?? store.queueWaiting)
const overdue = computed(() => routing.load?.queue.overdue ?? false)
const queue = computed(() => (queueCount.value > 0 ? `คิวกลาง ${queueCount.value} แชท` : 'คิวกลางว่าง'))

const BAR_STATES: MemberState[] = ['on', 'full']
const members = computed(() => {
  const live = routing.load?.members
  if (live) {
    const short = shortNames(live)
    return live.map((m) => {
      const isMe = m.id === store.meId
      const ratio = m.cap > 0 ? Math.min(1, m.load / m.cap) : 0
      const bar = BAR_STATES.includes(m.state)
      return {
        id: m.id,
        name: short.get(m.id) ?? m.name,
        isMe,
        ratio,
        bar,
        busy: bar && (m.state === 'full' || ratio >= 0.7),
        off: !bar,
        text: m.state_label,
        tip: isMe ? m.detail.replace(m.name, `${m.name} (คุณ)`) : m.detail,
      }
    })
  }
  return store.team.map((m) => {
    const isMe = m.id === store.meId
    const ratio = m.cap > 0 ? Math.min(1, m.open_count / m.cap) : 0
    const tip = m.available ? `${m.name}${isMe ? ' (คุณ)' : ''} · ดูแลอยู่ ${m.open_count} จาก ${m.cap}` : `${m.name} · พัก`
    return { id: m.id, name: m.name, isMe, ratio, bar: m.available, busy: ratio >= 0.7, off: !m.available, text: 'พัก', tip }
  })
})
</script>

<template>
  <footer class="lf">
    <div class="lf-1">
      <Icon name="users" />
      <template v-if="method === 'manual'"><b>{{ modeLabel }}</b><span>· ทีมหยิบเอง</span></template>
      <template v-else><span>แจกงานแบบ</span><b>{{ modeLabel }}</b></template>
      <span class="dotsep"></span>
      <span :class="{ late: overdue && queueCount > 0 }">{{ queue }}</span>
    </div>
    <div class="team">
      <div
        v-for="m in members"
        :key="m.id"
        class="tmb"
        :class="{ me: m.isMe, busy: m.busy, off: m.off }"
        role="group"
        :title="m.tip"
        :aria-label="m.tip"
      >
        <span class="tmb-n">{{ m.name }}</span>
        <span class="tmb-l">
          <span v-if="m.bar" class="tmb-bar"><span class="tmb-fill" :style="{ width: m.ratio * 100 + '%' }"></span></span>
          <span v-else class="tmb-off">{{ m.text }}</span>
        </span>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.lf {
  flex: none;
  border-top: 1px solid var(--line);
  padding: 12px 16px;
  background: #fafbfa;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.lf-1 {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
  white-space: nowrap;
  min-width: 0;
}
.lf-1 :deep(.ic) {
  width: 15px;
  height: 15px;
}
.lf-1 b {
  color: var(--ink);
  font-weight: 600;
}
.lf-1 .late {
  color: var(--warn);
  font-weight: 600;
}
.team {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 8px;
}
.tmb {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.tmb-n {
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tmb.me .tmb-n {
  color: var(--ac2);
  font-weight: 600;
}
.tmb.off .tmb-n {
  color: var(--mut);
}
.tmb-l {
  display: flex;
  align-items: center;
  height: 18px;
}
.tmb-bar {
  display: block;
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: #e1e8e4;
  overflow: hidden;
}
.tmb-fill {
  display: block;
  height: 100%;
  border-radius: 2px;
  background: var(--ac);
}
.tmb.busy .tmb-fill {
  background: #c98410;
}
.tmb-off {
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
</style>
